import { Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changePassword } from "../../redux/reducers/authSlice";
import { getVetDetailsById } from "../../redux/reducers/clinicSlice";
import { updateVetByClinic } from "../../redux/reducers/vetSlice";
import { newRoleList } from "../../util/arrayList";
import {
  accessList,
  clinicRolesList,
  salutationList,
  typeList,
} from "../../util/dropList";
import CustomButton from "../CustomButton";
import CustomModal from "../CustomModal/CustomModal";
import CustomTextField from "../CustomTextField";
import CustomUpload from "../CustomUpload";
import { default as Select } from "../Select/Select";

const requiredFields = ["name", "role"];

const initialValues = {
  type: "Vet",
  salutation: "Mr",
  name: "",
  speciality: null,
  contactNumber: "",
  conType: [],
  email: "",
  password: "",
  role: null,
  registrationNo: "",
  image: { file: null, previewUrl: null },
  access: [],
};

const initialError = {
  salutation: false,
  name: false,
  speciality: false,
  conType: false,
  contactNumber: false,
  email: false,
  password: false,
  role: false,
  access: false,
};

const initialHelp = {
  salutation: "",
  name: "",
  speciality: "",
  conType: "",
  contactNumber: "",
  email: "",
  password: "",
  role: "",
  access: "",
};

const nameExpan = {
  salutation: "Salutation",
  name: "Name",
  speciality: "Speciality",
  conType: "Consultation Type",
  contactNumber: "Contact Number",
  email: "Email",
  password: "Password",
  role: "role",
  access: "Access",
};

const initialPassValues = { oldPass: "", newPass: "", confirmPass: "" };
const initialPassHelps = { oldPass: "", newPass: "", confirmPass: "" };
const initialPassErrors = {
  oldPass: false,
  newPass: false,
  confirmPass: false,
};

const EditProfileModal = ({ open, onClose, vetId }) => {
  const dispatch = useDispatch();
  const signRef = useRef();
  const role = localStorage.getItem("role");
  const [signedFile, setSignedFile] = useState(null);
  const [profileValues, setProfileValues] = useState(initialValues);
  const [profileHelps, setProfileHelps] = useState(initialHelp);
  const [profileError, setProfileError] = useState(initialError);
  const [changePassVsble, setChangePassVsble] = useState(false);
  const [passValues, setPassValues] = useState(initialPassValues);
  const [passHelps, setPassHelps] = useState(initialPassHelps);
  const [passErrors, setPassErrors] = useState(initialPassErrors);
  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConPass, setShowConPass] = useState(false);
  const {
    facilities,
    vet: vetDetails,
    clinicRoles,
  } = useSelector((state) => state?.clinic);

  useEffect(() => {
    if (!open || !vetId) return;
    dispatch(getVetDetailsById(vetId));
    // dispatch(getClinicFacilities());
    // dispatch(getClinicRoles());
  }, [open, vetId, dispatch]);

  useEffect(() => {
    if (!open || !vetDetails) return;
    const resolvedVetId = vetDetails?.doctorId ?? vetDetails?.id;
    if (String(resolvedVetId) !== String(vetId)) return;
    populateProfileValues();
  }, [open, vetDetails, facilities, vetId]);

  const checkRoleDoctor = () => {
    const roleDoctor = vetDetails?.userType?.find(
      (usr) => usr?.name === "ROLE_DOCTOR",
    );
    return Boolean(roleDoctor);
  };

  const populateProfileValues = () => {
    const roleMap = {
      ROLE_BILLING: "Billing",
      ROLE_CLINICADMIN: "Admin",
      ROLE_DOCTOR: "Vet",
      ROLE_PHARMACY: "Pharmacy Technician",
      ROLE_RECEPTION: "Front Desk",
    };

    const hasDoctor = vetDetails?.userType?.some(
      (userRole) => userRole?.name === "ROLE_DOCTOR",
    );
    const hasClinicAdmin = vetDetails?.userType?.some(
      (userRole) => userRole?.name === "ROLE_CLINICADMIN",
    );

    const updatedUserType =
      hasDoctor && hasClinicAdmin
        ? vetDetails?.userType?.filter(
            (userRole) => userRole?.name !== "ROLE_CLINICADMIN",
          )
        : vetDetails?.userType;

    const accessSelected = updatedUserType?.map((ut) => {
      const roleLabel = roleMap[ut?.name];
      return accessList?.find((al) => al?.value === roleLabel)?.value;
    });
    const facMat = facilities?.find(
      (fl) => fl?.facilityId === vetDetails?.facility,
    );
    const reqFacility =
      vetDetails?.facility && facMat
        ? {
            name: facMat?.name,
            value: facMat?.facilityId ?? vetDetails?.facility,
            id: facMat?.facilityId ?? vetDetails?.facility,
          }
        : null;
    const reqConType = vetDetails?.consulationType?.map((li) => {
      const reqCon = typeList?.find(
        (tl) => tl?.value?.toLowerCase() === li?.toLowerCase(),
      );
      return reqCon?.value;
    });

    setProfileValues({
      ...initialValues,
      type: checkRoleDoctor() ? "Vet" : "Other",
      name: vetDetails?.name,
      speciality: Array.isArray(vetDetails?.speciality)
        ? vetDetails?.speciality?.map((li) => ({ name: li, value: li }))
        : vetDetails?.speciality
          ? [vetDetails?.speciality]
          : [],
      conType: reqConType ?? [],
      email: vetDetails?.email,
      role:
        (checkRoleDoctor() ? newRoleList : clinicRolesList)?.find(
          (rl) => rl?.value === vetDetails?.role,
        ) ?? null,
      access: accessSelected ?? [],
      salutation: checkRoleDoctor()
        ? null
        : vetDetails?.salutation?.length > 0
          ? { name: vetDetails?.salutation, value: vetDetails?.salutation }
          : null,
      contactNumber: vetDetails?.mobile,
      registrationNo: vetDetails?.registrationNo ?? "",
      image: { file: null, previewUrl: vetDetails?.image },
      signature: { file: null, previewUrl: vetDetails?.signature },
      department: vetDetails?.department
        ? {
            name: vetDetails?.department,
            value: vetDetails?.department,
          }
        : null,
      facility: reqFacility,
    });
    setProfileHelps(initialHelp);
    setProfileError(initialError);
    setSignedFile(null);
  };

  const handleClose = () => {
    setProfileValues(initialValues);
    setProfileHelps(initialHelp);
    setProfileError(initialError);
    setSignedFile(null);
    onCloseChangePassMod();
    onClose();
  };

  const handleChangePassword = () => {
    setPassValues(initialPassValues);
    setPassErrors(initialPassErrors);
    setPassHelps(initialPassHelps);
    setChangePassVsble(true);
  };

  const onCloseChangePassMod = () => {
    setChangePassVsble(false);
    setPassValues(initialPassValues);
    setPassErrors(initialPassErrors);
    setPassHelps(initialPassHelps);
    setShowOldPass(false);
    setShowNewPass(false);
    setShowConPass(false);
  };

  const handleChangePassVal = (e) => {
    const { name, value } = e?.target;
    setPassValues({ ...passValues, [name]: value });
    let err = value?.length >= 8 ? false : value?.length > 0 ? true : true;
    let hlp =
      value?.length >= 8
        ? ""
        : value?.length > 0
          ? "Password must be 8 characters"
          : "This is required field";
    if (name === "newPass") {
      hlp =
        value?.length >= 8
          ? passValues?.oldPass === value
            ? "Old & New passwords cannot be the same"
            : ""
          : value?.length > 0
            ? "Password must be 8 characters"
            : "This is required field";
      err =
        value?.length >= 8
          ? passValues?.oldPass === value
            ? true
            : false
          : value?.length > 0
            ? true
            : true;
    }
    if (name === "confirmPass") {
      hlp =
        passValues?.newPass === value
          ? ""
          : "Confirm password not match with new password";
      err = passValues?.newPass === value ? false : true;
    }
    setPassHelps({ ...passHelps, [name]: hlp });
    setPassErrors({
      ...passErrors,
      [name]: err,
    });
  };

  const onSaveChangePassword = () => {
    let errObj = {};
    let hlpObj = {};
    Object.keys(passValues).forEach((key) => {
      if (key === "oldPass") {
        hlpObj = {
          ...hlpObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? ""
              : passValues?.[key]?.length > 0
                ? "Password must be 8 characters"
                : "This is required field",
        };
        errObj = {
          ...errObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? false
              : passValues?.[key]?.length > 0
                ? true
                : true,
        };
      }
      if (key === "newPass") {
        hlpObj = {
          ...hlpObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.oldPass === passValues?.[key]
                ? "Old & New passwords cannot be the same"
                : ""
              : passValues?.[key]?.length > 0
                ? "Password must be 8 characters"
                : "This is required field",
        };
        errObj = {
          ...errObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.oldPass === passValues?.[key]
                ? true
                : false
              : passValues?.[key]?.length > 0
                ? true
                : true,
        };
      }
      if (key === "confirmPass") {
        hlpObj = {
          ...hlpObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.newPass === passValues?.[key]
                ? ""
                : "Confirm password not match with new password"
              : "This is required field",
        };
        errObj = {
          ...errObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.newPass === passValues?.[key]
                ? false
                : true
              : true,
        };
      }
    });
    setPassHelps({ ...passHelps, ...hlpObj });
    setPassErrors({ ...passErrors, ...errObj });

    if (Object.keys(errObj)?.some((val) => errObj?.[val] === true)) return;

    dispatch(
      changePassword({
        oldPassword: passValues?.oldPass,
        newPassword: passValues?.confirmPass,
      }),
    ).then((res) => {
      if (res?.payload) {
        if (res?.payload?.status === 406) {
          setPassHelps({ ...passHelps, oldPass: "Incorrect old password" });
          setPassErrors({ ...passErrors, oldPass: true });
        } else {
          onCloseChangePassMod();
        }
      }
    });
  };

  const handleChange = (name, value) => {
    if (name === "conType") {
      setProfileValues({
        ...profileValues,
        conType: typeof value === "string" ? value.split(",") : value,
      });
      return;
    }
    setProfileValues({
      ...profileValues,
      [name]:
        name === "name"
          ? value?.charAt(0).toUpperCase() + value?.slice(1)
          : value,
    });
    if (value === "") {
      setProfileError({ ...profileError, [name]: true });
      setProfileHelps({
        ...profileHelps,
        [name]: `${nameExpan?.[name]} Required Field`,
      });
    }
    if (value !== "") {
      setProfileError({ ...profileError, [name]: false });
      setProfileHelps({ ...profileHelps, [name]: "" });
    }
  };

  const handleValidate = () => {
    let errObj = {},
      hlpObj = {};
    [
      ...requiredFields,
      ...(checkRoleDoctor() ? ["access", "conType", "speciality"] : []),
    ].forEach((key) => {
      const reqValue =
        typeof profileValues?.[key] === "string"
          ? profileValues?.[key]
          : (profileValues?.[key]?.value ?? profileValues?.[key]);
      if (reqValue?.length === 0 || !reqValue) {
        errObj = { ...errObj, [key]: true };
        hlpObj = { ...hlpObj, [key]: "This Field is required" };
      }
    });
    setProfileError({ ...profileError, ...errObj });
    setProfileHelps({ ...profileHelps, ...hlpObj });
    return Object.keys(errObj).length > 0;
  };

  const dataURLtoFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  const handleUpdate = () => {
    const isNotValid = handleValidate();
    if (isNotValid) return;
    const form = new FormData();
    if (signedFile?.file) {
      form.append("signature", signedFile?.file);
    }
    form.append("name", profileValues.name);
    form.append("email", profileValues.email);
    form.append(
      "salutation",
      checkRoleDoctor() ? "" : (profileValues?.salutation?.value ?? ""),
    );
    if (checkRoleDoctor()) {
      form.append(
        "speciality",
        profileValues.speciality?.map((li) => li?.value),
      );
      form.append("consulationType", profileValues.conType);
    }
    form.append("mobile", profileValues?.contactNumber);
    form.append("role", profileValues?.role?.value);
    if (profileValues?.department?.value) {
      form.append("department", profileValues?.department?.value);
    }
    if (profileValues?.facility?.value) {
      form.append("facility", profileValues?.facility?.value);
    }
    if (checkRoleDoctor()) {
      form.append(
        "access",
        profileValues?.access?.map(
          (ac) => accessList?.find(({ value }) => value === ac)?.apiValue,
        ),
      );
    }
    if (profileValues?.registrationNo !== "") {
      form.append("registrationNo", profileValues?.registrationNo ?? "");
    }
    if (profileValues?.image?.file) {
      form.append("image", profileValues?.image?.file);
    }
    dispatch(updateVetByClinic({ vetId, form })).then((res) => {
      if (res?.payload) {
        dispatch(getVetDetailsById(vetId)).then((res2) => {
          if (res2?.payload)
            localStorage.setItem("user", JSON.stringify(res2?.payload));
        });
        handleClose();
      }
    });
  };

  const onUploadFile = (e) => {
    const reader = new FileReader();
    const file = e?.target?.files[0];
    reader.onloadend = () => {
      setProfileValues({
        ...profileValues,
        image: { file: e.target.files[0], previewUrl: reader.result },
      });
    };
    reader.readAsDataURL(file);
  };

  const handleMultiSelect = (event, name) => {
    const {
      target: { value },
    } = event;
    const reqValue =
      name === "access"
        ? value?.includes("Admin")
          ? ["Admin"]
          : value
        : value;
    setProfileValues({
      ...profileValues,
      [name]: reqValue,
    });
    setProfileError({
      ...profileError,
      [name]: reqValue?.length > 0 ? false : true,
    });
    setProfileHelps({
      ...profileHelps,
      [name]: reqValue?.length > 0 ? "" : `${nameExpan?.[name]} Required Field`,
    });
  };

  const getDepartments = () => {
    return clinicRoles
      ?.filter((li) => li?.type === "department")
      ?.map((li) => ({
        name: li?.roleName,
        value: li?.roleName,
      }));
  };

  return (
    <>
      <CustomModal
        open={open}
        onClose={handleClose}
        header="Edit Profile"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh">
          <Grid container spacing={1} className="ph20">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="mb30">
              <CustomUpload
                uploadText="Profile Picture"
                onUploadFile={onUploadFile}
                value={profileValues?.image?.previewUrl}
                profileImg
                imageHeight={140}
              />
            </Grid>
            {checkRoleDoctor() ? null : (
              <Grid item xs={4} sm={4} md={3} lg={3} xl={3}>
                <div>
                  <Select
                    list={salutationList}
                    value={profileValues?.salutation}
                    handleChange={(e, value) =>
                      handleChange("salutation", value)
                    }
                    name="salutation"
                    label="Salutation *"
                    search
                    error={profileError?.salutation}
                    helperText={profileHelps?.salutation}
                  />
                </div>
              </Grid>
            )}
            <Grid
              item
              xs={checkRoleDoctor() ? 12 : 8}
              sm={checkRoleDoctor() ? 12 : 8}
              md={checkRoleDoctor() ? 12 : 9}
              lg={checkRoleDoctor() ? 12 : 9}
              xl={checkRoleDoctor() ? 12 : 9}
            >
              <div>
                <CustomTextField
                  name="name"
                  fullWidth
                  handleChange={(e) => handleChange("name", e?.target?.value)}
                  required={true}
                  label="Name"
                  value={profileValues?.name}
                  helperText={profileHelps?.name}
                  error={profileError?.name}
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt20">
                <Select
                  list={checkRoleDoctor() ? newRoleList : clinicRolesList}
                  value={profileValues?.role}
                  handleChange={(e, value) => handleChange("role", value)}
                  error={profileError?.role}
                  helperText={profileHelps?.role}
                  label="Role *"
                  search
                />
              </div>
            </Grid>

            {/* <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mb10">
                <Select
                  list={getDepartments()}
                  value={profileValues?.department}
                  handleChange={(e, value) => handleChange("department", value)}
                  required
                  label="Department"
                  search
                  error={profileError?.department}
                  helperText={profileHelps?.department}
                  disabled={role !== "ROLE_CLINIC"}
                />
              </div>
            </Grid> */}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt20">
                <CustomTextField
                  value={profileValues?.contactNumber}
                  handleChange={(e) =>
                    handleChange("contactNumber", e?.target?.value)
                  }
                  name="Contact Number"
                  error={profileError?.contactNumber}
                  fullWidth
                  helperText={profileHelps?.contactNumber}
                  label="Phone Number"
                  disabled
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt20">
                <CustomTextField
                  name="email"
                  fullWidth
                  handleChange={(e) => handleChange("email", e?.target?.value)}
                  value={profileValues?.email}
                  helperText={profileHelps?.email}
                  error={profileError?.email}
                  label="Email"
                  required={true}
                  disabled
                />
              </div>
              <Typography
                className="fs14 blue-color font-bold cursor mt10"
                onClick={handleChangePassword}
              >
                Change Password
              </Typography>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex1-end">
                <div className="w20Per mt10">
                  <CustomButton text="Update" onClick={handleUpdate} smallBtn />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </CustomModal>

      <CustomModal
        open={changePassVsble}
        onClose={onCloseChangePassMod}
        header="Change Password"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh">
          <div className="mh20">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  label="Old Password"
                  name="oldPass"
                  fullWidth
                  handleChange={handleChangePassVal}
                  value={passValues?.oldPass}
                  error={passErrors?.oldPass}
                  helperText={passHelps?.oldPass}
                  showPassword={showOldPass}
                  handleClickShowPassword={() => setShowOldPass(!showOldPass)}
                  labelTop
                  mandatoryField
                  password
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  label="New Password"
                  name="newPass"
                  fullWidth
                  handleChange={handleChangePassVal}
                  value={passValues?.newPass}
                  error={passErrors?.newPass}
                  helperText={passHelps?.newPass}
                  showPassword={showNewPass}
                  handleClickShowPassword={() => setShowNewPass(!showNewPass)}
                  labelTop
                  mandatoryField
                  password
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  label="Confirm Password"
                  name="confirmPass"
                  fullWidth
                  handleChange={handleChangePassVal}
                  value={passValues?.confirmPass}
                  error={passErrors?.confirmPass}
                  helperText={passHelps?.confirmPass}
                  showPassword={showConPass}
                  handleClickShowPassword={() => setShowConPass(!showConPass)}
                  labelTop
                  mandatoryField
                  password
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-end mt20">
                <div>
                  <CustomButton
                    text="Save"
                    onClick={onSaveChangePassword}
                    smallBtn
                  />
                </div>
              </div>
            </Grid>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

EditProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

EditProfileModal.defaultProps = {
  vetId: null,
};

export default EditProfileModal;
